import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon,
  Bars3Icon,
  UserCircleIcon,
  ChevronDownIcon,
  Cog6ToothIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import Button from '../common/Button';
import SearchBar from '../prompts/SearchBar';
import Badge from '../common/Badge';
import AuthModal from '../auth/AuthModal';
import PricingModal from '../subscription/PricingModal';
import UserProfileModal from '../profile/UserProfileModal';

interface HeaderProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: (query: string) => void;
  onMenuToggle: () => void;
  isMobileMenuOpen: boolean;
  onOpenPricing: () => void;
}

const Header: React.FC<HeaderProps> = ({
  searchValue,
  onSearchChange,
  onSearch,
  onMenuToggle,
  isMobileMenuOpen,
  onOpenPricing,
}) => {
  const { user, logout } = useAuth();
  const { profile, isProfileComplete } = useUserProfile();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const getSubscriptionBadge = (type: string) => {
    switch (type) {
      case 'basic':
        return <Badge variant="primary" size="sm">Basic</Badge>;
      case 'pro':
        return <Badge variant="warning" size="sm">Pro</Badge>;
      default:
        return <Badge variant="secondary" size="sm">Free</Badge>;
    }
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* 로고 및 모바일 메뉴 버튼 */}
            <div className="flex items-center">
              <button
                onClick={onMenuToggle}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              <div className="flex-shrink-0 flex items-center ml-2 lg:ml-0">
                <h1 className="text-xl font-bold text-primary-600">
                  EasyGPT
                </h1>
                <span className="ml-2 text-sm text-gray-500">
                  프롬프트 라이브러리
                </span>
              </div>
            </div>

            {/* 검색 바 (데스크톱) */}
            <div className="hidden md:block flex-1 max-w-lg mx-8">
              <SearchBar
                value={searchValue}
                onChange={onSearchChange}
                onSearch={onSearch}
                placeholder="프롬프트 검색..."
              />
            </div>

            {/* 우측 메뉴 */}
            <div className="flex items-center space-x-4">
              {/* 모바일 검색 버튼 */}
              <button className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100">
                <MagnifyingGlassIcon className="h-6 w-6" />
              </button>

              {/* 프로필 설정 버튼 */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProfileModal(true)}
                className="hidden sm:flex items-center gap-2"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span>프로필 설정</span>
                {!isProfileComplete() && (
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </Button>

              {/* 프리미엄 버튼 */}
              {(!user || user.subscription_type === 'free') && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="hidden sm:block"
                  onClick={onOpenPricing}
                >
                  PRO 업그레이드
                </Button>
              )}

              {/* 사용자 메뉴 */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                    <span className="hidden sm:ml-2 sm:block text-sm text-gray-700">
                      {user.username}
                    </span>
                    <div className="hidden sm:ml-1 sm:block">
                      {getSubscriptionBadge(user.subscription_type)}
                    </div>
                    <ChevronDownIcon className="hidden sm:ml-1 sm:block h-4 w-4 text-gray-500" />
                  </button>

                  {showUserMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowUserMenu(false)}
                      />
                      <div className="absolute right-0 z-20 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                        <div className="px-4 py-2 text-sm text-gray-500 border-b">
                          {user.email}
                        </div>
                        <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          프로필 설정
                        </a>
                        <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          사용 통계
                        </a>
                        {user.subscription_type === 'free' && (
                          <button
                            onClick={onOpenPricing}
                            className="block w-full text-left px-4 py-2 text-sm text-primary-600 hover:bg-gray-100"
                          >
                            업그레이드
                          </button>
                        )}
                        <button
                          onClick={logout}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          로그아웃
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAuthClick('login')}
                  >
                    로그인
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAuthClick('signup')}
                  >
                    시작하기
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* 모바일 검색 바 */}
          <div className="md:hidden pb-4">
            <SearchBar
              value={searchValue}
              onChange={onSearchChange}
              onSearch={onSearch}
              placeholder="프롬프트 검색..."
            />
          </div>
        </div>
      </header>

      {/* 인증 모달 */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      {/* 프로필 모달 */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </>
  );
};

export default Header;